import React from 'react';
import { Github, Linkedin, Mail, Heart, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Projects', href: '#projects' },
    { name: 'Experience', href: '#experience' },
    { name: 'Education', href: '#education' },
    { name: 'Contact', href: '#contact' }
  ];

  const socialLinks = [
    {
      name: 'GitHub',
      href: 'https://github.com/zaidharissheikh',
      icon: <Github className="w-5 h-5" />,
      color: 'hover:text-gray-400'
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/zaid-haris-saigal-680967248/',
      icon: <Linkedin className="w-5 h-5" />,
      color: 'hover:text-blue-400'
    },
    {
      name: 'Email',
      href: 'mailto:zaidsaigal2005@gmail.com', 
      icon: <Mail className="w-5 h-5" />,
      color: 'hover:text-cyan-400'
    }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand Section */}
            <div className="md:col-span-2">
              <div className="text-3xl font-bold text-white mb-4">
                <span className="text-cyan-400">Z</span>aid<span className="text-cyan-400">H</span>aris
              </div>
              <p className="text-gray-400 text-lg mb-6 max-w-md">
                MERN Stack Developer passionate about creating innovative web solutions 
                and bringing ideas to life through clean, efficient code.
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target={link.href.startsWith('http') ? '_blank' : '_self'}
                    rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''}
                    className={`text-gray-400 ${link.color} transition-all duration-300 hover:transform hover:scale-110`}
                    aria-label={link.name}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-400 hover:text-cyan-400 transition-colors duration-300"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Get In Touch</h3>
              <div className="space-y-3">
                <a
                  href="mailto:zaidsaigal2005@gmail.com"
                  className="flex items-center text-gray-400 hover:text-cyan-400 transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="text-sm">zaidsaigal2005@gmail.com</span>
                </a>
                <div className="text-gray-400 text-sm">
                  <span className="text-cyan-400">Available for:</span>
                  <ul className="ml-4 mt-1 space-y-1">
                    <li>• Freelance Projects</li>
                    <li>• Full-time Opportunities</li>
                    <li>• Collaborations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              {/* Copyright */}
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                <span className="flex items-center">
                  © {currentYear} Zaid Haris. Made with 
                  <Heart className="w-4 h-4 text-red-500 mx-1" />
                  and lots of coffee
                </span>
              </div>

              {/* Tech Stack Badge */}
              <div className="flex items-center space-x-4">
                <div className="text-gray-400 text-sm">
                  Built with <span className="text-cyan-400">React</span> & <span className="text-purple-400">TailwindCSS</span>
                </div>
                
                {/* Back to Top Button */}
                <button
                  onClick={scrollToTop}
                  className="group bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-cyan-400 p-2 rounded-full transition-all duration-300 hover:transform hover:scale-110"
                  aria-label="Back to top"
                >
                  <ArrowUp className="w-4 h-4 group-hover:animate-bounce" />
                </button>
              </div>
            </div>
          </div>

          {/* Fun Footer Message */}
          <div className="mt-8 text-center">
            <div className="inline-block bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm rounded-lg px-6 py-3 border border-gray-700">
              <p className="text-gray-400 text-sm">
                <span className="text-cyan-400">Fun Fact:</span> This portfolio was built with passion, 
                precision, and probably too much caffeine ☕
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;